import React, {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {BasicUser} from '../../app/types'
import {api} from '../../common/api'
import {useSelector} from 'react-redux'
import {selectedSiteSelector} from '../../authentication/redux'
import SpinnableSubmitButton from "../../common/form/SpinnableSubmitButton";
import CancelButton from "../../common/form/CancelButton";
import {Dialog, DialogActions, DialogContent, DialogTitle, Paper} from "@mui/material";
import ErrorAlert from "../../common/components/ErrorAlert";
import {useTableWithAdjustableOrder} from "../../common/components/table/useTableWithAdjustableOrder";
import {formatUserInDropdown} from "../../common/util/formatters";
import {getAutocompleteUsers} from "../../common/util/helpers";
import {useFormSubmission} from "../../common/servercall/useFormSubmission";
import {useForm} from "react-hook-form";
import FormRow from "../../common/form/FormRow";
import FormFieldBox from "../../common/form/FormFieldBox";
import FormGroup from "../../common/form/FormGroup";
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import * as yup from "yup";

type AddAgentsForm = {
    userIds: BasicUser[]
}

export const AddAgents = ({onClose}: {onClose: () => void}) => {
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)

    const { control, handleSubmit, setError } = useForm<AddAgentsForm>({
        resolver: yupResolver(yup.object()),
        defaultValues: { userIds: [] },
    })

    const onSubmit = useCallback(
        async (formData: AddAgentsForm) => {
            if (!site ) return
            if (formData.userIds.length !== 0) {
                await api.post(`/api/site/${site.productId}/amcc/agents`, {userIds: formData.userIds.map(it => it.uuid)})
            }

            onClose()
        },
        [site, onClose]
    )

    const userOptionParams = useMemo(() =>
            getAutocompleteUsers(
                t('flows.edit.no-users-found'),
                [],
                true,
                site)
        , [site, t])

    const { submit, isSubmitting, serverError } = useFormSubmission<AddAgentsForm>(onSubmit, setError)

    const { selector: AgentSelector, table: Table } = useTableWithAdjustableOrder<BasicUser>('userIds', userOptionParams, control, formatUserInDropdown, user => user.uuid, t('users.edit.user-select-button'), true)

    return (
        <Dialog open={true} onClose={onClose} fullWidth={true} maxWidth={'sm'}>
            <form onSubmit={handleSubmit(submit)}>
                <DialogTitle>{t('contact-centers.agents.add')}</DialogTitle>
                <DialogContent>
                    <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError}/>
                    <FormGroup label={t('flows.edit.section.agents')} fullWidth={true}>
                        <FormRow>
                            <FormFieldBox mdSize={8} lgSize={6}>
                                {AgentSelector}
                            </FormFieldBox>
                        </FormRow>
                        <FormRow>
                            <FormFieldBox mdSize={12}>
                                <Paper elevation={2}>
                                    <Table />
                                </Paper>
                            </FormFieldBox>
                        </FormRow>
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <SpinnableSubmitButton label={t('general.add')} showSpinner={isSubmitting} />
                    <CancelButton onClick={onClose} />
                </DialogActions>
            </form>
        </Dialog>
    )
}
